import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { doc, getDoc, onSnapshot, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import { FirebaseError } from 'firebase/app'
import type { User } from 'firebase/auth'
import { firestore } from '@/firebase'
import { useAuthStore } from './auth'
import {
  STAFF_SESSIONS_COLLECTION,
  STAFF_SESSION_HEARTBEAT_MS,
  STAFF_INACTIVITY_MS,
  STAFF_SESSION_FIELD_LAST_ACTIVITY,
  STAFF_SESSION_FIELD_SESSION,
  STAFF_SESSION_ID_CLAIM,
} from '@gh/common/utils'
import createLogger from '@/utils/logger'

const logger = createLogger('stores/staffSession.ts')

/** Coalesce `pointermove` to avoid work on every move event (separate from `STAFF_SESSION_HEARTBEAT_MS`). */
const POINTER_MOVE_ACTIVITY_THROTTLE_MS = 1_000

/** Only when we can prove it from a readable doc + `lastActivityAt` (see `signOutErrorMessageForDeniedHeartbeat`). */
const MSG_INACTIVITY =
  'You were signed out because your staff session ended due to inactivity. Please sign in again.'

/**
 * When `updateDoc` fails, Firestore does not say why. We only show a specific sub-reason
 * (inactivity) if we can prove it. Everything else uses this neutral copy.
 */
const MSG_SESSION_ENDED = 'Your staff session is no longer valid. Please sign in again.'

const MSG_SIGNED_IN_ELSEWHERE =
  'You were signed out because this account was signed in elsewhere. Please sign in again if you need access.'

function isStaffSessionAuthError(err: unknown): boolean {
  return err instanceof FirebaseError && err.code === 'permission-denied'
}

async function signOutErrorMessageForDeniedHeartbeat(user: User): Promise<string> {
  try {
    try {
      await user.getIdTokenResult(true)
    } catch (err) {
      logger.warn('Staff session: ID token could not be refreshed; session likely invalid', err)
      return MSG_SESSION_ENDED
    }

    const snap = await getDoc(doc(firestore, STAFF_SESSIONS_COLLECTION, user.uid))
    if (snap.exists()) {
      const last = snap.data()?.lastActivityAt
      if (last instanceof Timestamp) {
        if (Date.now() - last.toMillis() > STAFF_INACTIVITY_MS) {
          return MSG_INACTIVITY
        }
      }
    }
  } catch (err) {
    if (isStaffSessionAuthError(err)) {
      logger.warn('Staff session: ID token could not be refreshed; session likely invalid', err)
      return MSG_SESSION_ENDED
    }
    logger.error('Failed to classify staff session sign-out', err)
  }
  return MSG_SESSION_ENDED
}

/**
 * Client staff session lifecycle: Firestore `staffSessions/{uid}`; this store bumps
 * `lastActivityAt` on **real** user activity (throttled), not on a timer — including
 * pointer movement while the tab is visible. Rules and Functions enforce policy; this
 * store signs out on denied writes.
 */
export const useStaffSessionStore = defineStore('staffSession', () => {
  const authStore = useAuthStore()

  /** True while activity listeners are registered (staff signed in and eligible). */
  const heartbeatActive = ref(false)
  /** True while `staffSessions/{uid}` is watched for session rotation (another sign-in). */
  const sessionDocSyncActive = ref(false)
  let activityController: AbortController | undefined
  let sessionDocUnsubscribe: (() => void) | undefined
  /** Avoid re-entrancy if snapshot + heartbeat both trigger sign-out. */
  let sessionSupersededSignOutInFlight = false
  let inactivitySignOutInFlight = false
  let inactivityTimeoutId: number | undefined
  let lastKnownActivityAtMs: number | undefined
  let lastBeatAt = 0
  /** Last time we treated pointer move as input (1 Hz coalescing). */
  let lastPointerMoveActivityAt = 0

  function clearInactivityTimer() {
    if (inactivityTimeoutId !== undefined) {
      window.clearTimeout(inactivityTimeoutId)
      inactivityTimeoutId = undefined
    }
  }

  async function signOutDueToInactivity() {
    if (
      inactivitySignOutInFlight ||
      authStore.isSigningOut ||
      !authStore.isSystemUser ||
      !authStore.user
    ) {
      return
    }
    inactivitySignOutInFlight = true
    await authStore.signOut({
      redirectToLogin: true,
      errorMessage: MSG_INACTIVITY,
      hardReloadToLogin: true,
    })
  }

  function scheduleInactivityLogout(lastActivityAtMs: number) {
    lastKnownActivityAtMs = lastActivityAtMs
    clearInactivityTimer()

    const remainingMs = lastActivityAtMs + STAFF_INACTIVITY_MS - Date.now()
    if (remainingMs <= 0) {
      void signOutDueToInactivity()
      return
    }

    inactivityTimeoutId = window.setTimeout(() => {
      void signOutDueToInactivity()
    }, remainingMs)
  }

  async function ensureStillActiveBeforeHeartbeat(): Promise<boolean> {
    if (lastKnownActivityAtMs !== undefined) {
      if (Date.now() - lastKnownActivityAtMs > STAFF_INACTIVITY_MS) {
        await signOutDueToInactivity()
        return false
      }
      return true
    }

    if (!authStore.user) {
      return false
    }

    const snap = await getDoc(doc(firestore, STAFF_SESSIONS_COLLECTION, authStore.user.uid))
    const last = snap.data()?.[STAFF_SESSION_FIELD_LAST_ACTIVITY]
    if (last instanceof Timestamp) {
      scheduleInactivityLogout(last.toMillis())
      if (Date.now() - last.toMillis() > STAFF_INACTIVITY_MS) {
        await signOutDueToInactivity()
        return false
      }
    }
    return true
  }

  function stopSessionDocWatch() {
    sessionDocUnsubscribe?.()
    sessionDocUnsubscribe = undefined
    sessionDocSyncActive.value = false
    sessionSupersededSignOutInFlight = false
    inactivitySignOutInFlight = false
    lastKnownActivityAtMs = undefined
    clearInactivityTimer()
  }

  /**
   * Live `staffSessions/{uid}`: if `currentSession` no longer matches this tab’s `ghStaffSid`
   * (after one token refresh to catch up right after sign-in), another sign-in revoked this session.
   */
  function startSessionDocWatch(uid: string) {
    stopSessionDocWatch()
    sessionDocSyncActive.value = true
    const sessionRef = doc(firestore, STAFF_SESSIONS_COLLECTION, uid)
    sessionDocUnsubscribe = onSnapshot(
      sessionRef,
      async (snap) => {
        if (sessionSupersededSignOutInFlight || authStore.isSigningOut) {
          return
        }
        if (!authStore.isSystemUser || authStore.user?.uid !== uid) {
          return
        }
        const user = authStore.user
        if (!snap.exists()) {
          logger.warn('Staff session doc missing; signing out')
          sessionSupersededSignOutInFlight = true
          await authStore.signOut({
            redirectToLogin: true,
            errorMessage: MSG_SESSION_ENDED,
            hardReloadToLogin: true,
          })
          return
        }
        const docSid = snap.data()?.[STAFF_SESSION_FIELD_SESSION]
        const lastActivityAt = snap.data()?.[STAFF_SESSION_FIELD_LAST_ACTIVITY]
        if (lastActivityAt instanceof Timestamp) {
          scheduleInactivityLogout(lastActivityAt.toMillis())
        }
        if (typeof docSid !== 'string' || docSid.length === 0) {
          return
        }
        const readClaim = async () => {
          const t = await user.getIdTokenResult(false)
          const c = t.claims[STAFF_SESSION_ID_CLAIM]
          return typeof c === 'string' ? c : ''
        }
        let claimSid = await readClaim()
        if (claimSid === docSid) {
          return
        }
        try {
          await user.getIdToken(true)
        } catch (err) {
          logger.warn('Staff session superseded: token refresh failed after doc rotation', err)
        }
        claimSid = await readClaim()
        if (claimSid !== docSid) {
          if (sessionSupersededSignOutInFlight) {
            return
          }
          sessionSupersededSignOutInFlight = true
          logger.warn('Staff session superseded: Firestore session id does not match this tab', {
            docSessionPrefix: docSid.slice(0, 8),
            claimSessionPrefix: claimSid.length > 0 ? claimSid.slice(0, 8) : '(none)',
          })
          await authStore.signOut({
            redirectToLogin: true,
            errorMessage: claimSid.length > 0 ? MSG_SIGNED_IN_ELSEWHERE : MSG_SESSION_ENDED,
            hardReloadToLogin: true,
          })
        }
      },
      (err) => {
        logger.error('Staff session doc listener error', err)
      },
    )
  }

  async function updateLastActivityInFirestore(): Promise<void> {
    if (!authStore.isSystemUser || !authStore.user) {
      return
    }
    const user = authStore.user
    try {
      // Ensure the token is usable; Firebase Auth refreshes only if the cached token is expired.
      await user.getIdToken()
      await updateDoc(doc(firestore, STAFF_SESSIONS_COLLECTION, user.uid), {
        lastActivityAt: serverTimestamp(),
      })
      scheduleInactivityLogout(Date.now())
    } catch (err) {
      if (isStaffSessionAuthError(err)) {
        if (authStore.isSigningOut) {
          return
        }
        logger.warn('Staff session heartbeat denied; signing out', err)
        const errorMessage = await signOutErrorMessageForDeniedHeartbeat(user)
        await authStore.signOut({
          redirectToLogin: true,
          errorMessage,
          hardReloadToLogin: true,
        })
        return
      }
      logger.error('Staff session update failed', err)
    }
  }

  /**
   * One Firestore bump without the inter-beat throttle (e.g. manual “touch session”).
   * Same sign-out flow as the scheduled heartbeat on `permission-denied`.
   */
  function bumpLastActivity(): Promise<void> {
    return updateLastActivityInFirestore()
  }

  /** Throttled: records activity only if enough time passed since the last write attempt. */
  const onUserActivity = async () => {
    if (!authStore.isSystemUser || !authStore.user?.uid) {
      return
    }
    if (document.visibilityState !== 'visible') {
      return
    }
    const now = Date.now()
    if (now - lastBeatAt < STAFF_SESSION_HEARTBEAT_MS) {
      return
    }
    if (!(await ensureStillActiveBeforeHeartbeat())) {
      return
    }
    lastBeatAt = now
    await updateLastActivityInFirestore()
  }

  function startHeartbeat() {
    if (activityController !== undefined) {
      return
    }
    activityController = new AbortController()
    const { signal } = activityController
    const options: AddEventListenerOptions = { capture: true, signal }
    const bump = () => {
      void onUserActivity()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void ensureStillActiveBeforeHeartbeat()
      }
    }
    const onPointerMoveWhileTabVisible = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      const t = Date.now()
      if (t - lastPointerMoveActivityAt < POINTER_MOVE_ACTIVITY_THROTTLE_MS) {
        return
      }
      lastPointerMoveActivityAt = t
      void onUserActivity()
    }
    window.addEventListener('pointerdown', bump, options)
    window.addEventListener('pointermove', onPointerMoveWhileTabVisible, {
      ...options,
      passive: true,
    })
    window.addEventListener('keydown', bump, options)
    window.addEventListener('scroll', bump, { ...options, passive: true })
    window.addEventListener('wheel', bump, { ...options, passive: true })
    document.addEventListener('visibilitychange', onVisibilityChange, { signal })
    heartbeatActive.value = true
  }

  function stopHeartbeat() {
    activityController?.abort()
    activityController = undefined
    heartbeatActive.value = false
    lastBeatAt = 0
    lastPointerMoveActivityAt = 0
  }

  watch(
    () => [authStore.isSystemUser, authStore.user?.uid] as const,
    ([isStaff, uid]) => {
      if (isStaff && uid) {
        startHeartbeat()
        startSessionDocWatch(uid)
      } else {
        stopHeartbeat()
        stopSessionDocWatch()
      }
    },
    { immediate: true },
  )

  function onUnmounted() {
    stopHeartbeat()
    stopSessionDocWatch()
  }

  return {
    heartbeatActive,
    sessionDocSyncActive,
    bumpLastActivity,
    onUnmounted,
  }
})
